import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { writeInspectorWidth } from '../designer/inspectorWidth';
import { PHONE_QUERY, useMediaQuery } from '../shell/breakpoints';
import { isTypingTarget, modalOpen } from '../shell/shortcuts';
import { strings } from '../strings';
import { DockContext, firstControl, focusedWindow, frameOf, type DockApi } from './context';
import {
  defaultLayout,
  dockReducer,
  effectiveLayout,
  GEOMETRY,
  isSide,
  phoneLayout,
  type DockAction,
  type DockContext as ReducerContext,
  type DockState,
  type DockZone,
  type Rect,
  type Size,
} from './layout';
import { readDock, writeDock } from './persistence';
import { TOOL_WINDOWS, windowDefaults, type ToolWindowBadge, type ToolWindowDefinition } from './registry';

const NARROW_QUERY = `(max-width: ${GEOMETRY.narrow - 0.02}px)`;

function subscribeNarrow(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => {};
  const query = window.matchMedia(NARROW_QUERY);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}

function isNarrow(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(NARROW_QUERY).matches;
}

interface Provider {
  token: string;
  badge: ToolWindowBadge | undefined;
}

interface FocusIntent {
  id: string;
  /** Focus this element again if it is still in the window after the change. */
  element: Element | null;
}

const s = strings.dock;
const ARROW_ZONES: Record<string, DockZone | 'float'> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
  ArrowDown: 'bottom',
  ArrowUp: 'float',
};

/** Where focus goes when the focused window is hidden and its opener is gone: the page's h1, else main. */
function pageFocusTarget(): HTMLElement | null {
  const main = document.getElementById('main');
  const heading = main?.querySelector<HTMLElement>('h1');
  if (heading) {
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    return heading;
  }
  return main;
}

/**
 * The docking framework's state and commands (spec 6.8): the layout of `user` (read from and written to
 * `localStorage`), the windows the page provides, announcements, focus handling and the keyboard shortcuts.
 */
export function DockProvider({
  user,
  definitions = TOOL_WINDOWS,
  children,
}: {
  user: string | null;
  definitions?: readonly ToolWindowDefinition[];
  children: ReactNode;
}) {
  const defaults = useMemo(() => defaultLayout(windowDefaults(definitions)), [definitions]);
  const [state, setState] = useState<DockState>(() => ({
    layout: readDock(user, defaults),
    maximized: null,
  }));
  const narrow = useSyncExternalStore(subscribeNarrow, isNarrow, () => false);
  const phone = useMediaQuery(PHONE_QUERY);
  // On phones one window at a time is open as a bottom sheet; it isn't part of the stored layout. The sheet
  // belongs to the content provider that was current when it opened, so it doesn't pop up on another page.
  const [sheetState, setSheetState] = useState<{ id: string; token: string | undefined } | null>(null);
  const [workspace, setWorkspaceState] = useState<Size>(() => ({
    width: Math.max(0, window.innerWidth - 72),
    height: Math.max(0, window.innerHeight - 74),
  }));
  const setWorkspace = useCallback(
    (size: Size) =>
      setWorkspaceState((current) =>
        current.width === size.width && current.height === size.height ? current : size,
      ),
    [],
  );
  const [providers, setProviders] = useState<ReadonlyMap<string, readonly Provider[]>>(() => new Map());
  const [announcement, setAnnouncement] = useState({ text: '', n: 0 });

  const reducerContext = useRef<ReducerContext>({ defaults, narrow });
  useEffect(() => {
    reducerContext.current = { defaults, narrow };
  }, [defaults, narrow]);

  const dispatch = useCallback((action: DockAction) => {
    setState((current) => dockReducer(current, action, reducerContext.current));
  }, []);

  // Persist every change of the layout (not the one read at start).
  const written = useRef(state.layout);
  useEffect(() => {
    if (state.layout === written.current) return;
    written.current = state.layout;
    writeDock(user, state.layout);
  }, [user, state.layout]);

  const available = useMemo(
    () => new Set([...providers].filter(([, list]) => list.length > 0).map(([id]) => id)),
    [providers],
  );
  const badges = useMemo(
    () =>
      new Map(
        [...providers]
          .filter(([, list]) => list.length > 0)
          .map(([id, list]) => [id, list[list.length - 1].badge ?? {}]),
      ),
    [providers],
  );
  const currentToken = (id: string) => {
    const list = providers.get(id);
    return list && list.length > 0 ? list[list.length - 1].token : undefined;
  };
  const sheet =
    sheetState && available.has(sheetState.id) && currentToken(sheetState.id) === sheetState.token
      ? sheetState.id
      : null;
  const effective = useMemo(
    () => (phone ? phoneLayout(sheet, available) : effectiveLayout(state, available, narrow)),
    [phone, sheet, state, available, narrow],
  );

  // ---- Content registration
  const register = useCallback((id: string, token: string) => {
    setProviders((current) => {
      const next = new Map(current);
      next.set(id, [
        ...(current.get(id) ?? []).filter((p) => p.token !== token),
        { token, badge: undefined },
      ]);
      return next;
    });
    return () =>
      setProviders((current) => {
        const next = new Map(current);
        next.set(
          id,
          (current.get(id) ?? []).filter((p) => p.token !== token),
        );
        return next;
      });
  }, []);
  const setBadge = useCallback((id: string, token: string, badge: ToolWindowBadge | undefined) => {
    setProviders((current) => {
      const list = current.get(id);
      if (!list?.some((p) => p.token === token)) return current;
      const next = new Map(current);
      next.set(
        id,
        list.map((p) => (p.token === token ? { token, badge } : p)),
      );
      return next;
    });
  }, []);
  const providerOf = useCallback(
    (id: string) => {
      const list = providers.get(id);
      return list && list.length > 0 ? list[list.length - 1].token : undefined;
    },
    [providers],
  );
  const hosts = useRef(new Map<string, HTMLElement>());
  const host = useCallback((id: string) => {
    let element = hosts.current.get(id);
    if (!element) {
      element = document.createElement('div');
      element.className = 'tw-host';
      hosts.current.set(id, element);
    }
    return element;
  }, []);

  // ---- Announcements (spec 6.8.7)
  const announce = useCallback((text: string) => setAnnouncement((a) => ({ text, n: a.n + 1 })), []);

  // ---- Focus: after a change, focus lands in the window, or returns to its opener when it was hidden.
  const openers = useRef(new Map<string, Element>());
  const [focusIntent, setFocusIntent] = useState<{
    intent: FocusIntent | null;
    restore: string | null;
    n: number;
  }>({ intent: null, restore: null, n: 0 });
  useEffect(() => {
    const { intent, restore } = focusIntent;
    if (restore) {
      const opener = openers.current.get(restore);
      openers.current.delete(restore);
      const target =
        opener instanceof HTMLElement && opener.isConnected && !opener.closest('[inert]')
          ? opener
          : pageFocusTarget();
      target?.focus();
      return;
    }
    if (!intent) return;
    let frames = 0;
    let handle = 0;
    const start = document.activeElement;
    const attempt = () => {
      const now = document.activeElement;
      // The user moved focus elsewhere in the meantime: leave it there.
      if (now !== start && now !== document.body && now !== null && !frameOf(intent.id)?.contains(now))
        return;
      const frame = frameOf(intent.id);
      if (frame && frame.querySelector('[data-tool-window-body] > .tw-host')) {
        const previous = document.activeElement;
        if (!frame.contains(previous) && previous instanceof Element && previous !== document.body) {
          openers.current.set(intent.id, previous);
        }
        const keep = intent.element;
        if (keep instanceof HTMLElement && keep.isConnected && frame.contains(keep)) keep.focus();
        else firstControl(frame).focus();
        return;
      }
      if (++frames < 20) handle = requestAnimationFrame(attempt);
    };
    handle = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(handle);
  }, [focusIntent]);

  const focusWindow = useCallback((id: string, element: Element | null = null) => {
    setFocusIntent((f) => ({ intent: { id, element }, restore: null, n: f.n + 1 }));
  }, []);

  // ---- Commands
  const latest = useRef({ state, available, effective, narrow, phone, sheet, providers, definitions });
  useEffect(() => {
    latest.current = { state, available, effective, narrow, phone, sheet, providers, definitions };
  });
  const title = useCallback(
    (id: string) => latest.current.definitions.find((d) => d.id === id)?.title ?? id,
    [],
  );
  /** The focused element when it is in window `id` (so a change can keep focus there). */
  const keepFocus = (id: string) => (focusedWindow() === id ? document.activeElement : undefined);

  const api = useMemo<DockApi>(() => {
    const unavailable = (id: string) => {
      if (latest.current.available.has(id)) return false;
      const definition = latest.current.definitions.find((d) => d.id === id);
      if (definition) announce(definition.unavailable);
      return true;
    };
    const needsWidth = () => {
      if (!latest.current.narrow) return false;
      announce(s.announce.narrow);
      return true;
    };
    const after = (id: string, element: Element | null | undefined) => {
      if (element !== undefined) focusWindow(id, element);
    };

    const show: DockApi['show'] = (id, options) => {
      if (unavailable(id)) return;
      if (latest.current.phone) {
        // The sheet is modal: it always takes focus.
        if (latest.current.sheet !== id) announce(s.announce.shown(title(id)));
        latest.current = { ...latest.current, sheet: id };
        const list = latest.current.providers.get(id);
        setSheetState({ id, token: list?.[list.length - 1]?.token });
        focusWindow(id);
        return;
      }
      const wasHidden = !latest.current.state.layout.windows[id]?.visible;
      dispatch({ type: 'show', id });
      if (wasHidden) announce(s.announce.shown(title(id)));
      if (options?.focus) focusWindow(id);
    };
    const hide: DockApi['hide'] = (id) => {
      if (latest.current.phone) {
        if (latest.current.sheet !== id) return;
        latest.current = { ...latest.current, sheet: null };
        setSheetState(null);
        announce(s.announce.hidden(title(id), null));
        setFocusIntent((f) => ({ intent: null, restore: id, n: f.n + 1 }));
        return;
      }
      const wasFocused = focusedWindow() === id;
      dispatch({ type: 'hide', id });
      const digit = latest.current.definitions.find((d) => d.id === id)?.shortcut;
      announce(s.announce.hidden(title(id), digit ? s.spokenKeys(digit) : null));
      if (wasFocused) setFocusIntent((f) => ({ intent: null, restore: id, n: f.n + 1 }));
    };
    return {
      definitions,
      state,
      effective,
      available,
      badges,
      narrow,
      phone,
      sheet,
      workspace,
      setWorkspace,
      toggle: (id) => {
        if (unavailable(id)) return;
        if (latest.current.phone) {
          if (latest.current.sheet === id) hide(id);
          else show(id);
          return;
        }
        if (!latest.current.effective.displayed.has(id)) show(id, { focus: true });
        else if (focusedWindow() !== id) focusWindow(id);
        else hide(id);
      },
      show,
      hide,
      dock: (id, zone, index) => {
        if (isSide(zone) && needsWidth()) return;
        const element = keepFocus(id);
        dispatch({ type: 'dock', id, zone, index });
        announce(s.announce.docked(title(id), zone));
        after(id, element);
      },
      float: (id, rect) => {
        if (needsWidth()) return;
        const element = keepFocus(id);
        dispatch({ type: 'float', id, rect });
        announce(s.announce.floating(title(id)));
        after(id, element);
      },
      toggleMaximize: (id) => {
        const element = keepFocus(id);
        if (latest.current.state.maximized === id) {
          dispatch({ type: 'restore' });
          announce(s.announce.restored(title(id)));
        } else {
          dispatch({ type: 'maximize', id });
          announce(s.announce.maximized(title(id)));
        }
        after(id, element);
      },
      activate: (id) => dispatch({ type: 'activate', id }),
      resize: (zone, size) => dispatch({ type: 'resize', zone, size }),
      collapse: (zone, collapsed) => {
        dispatch({ type: 'collapse', zone, collapsed });
        announce(collapsed ? s.announce.collapsed(s.zone[zone]) : s.announce.expanded(s.zone[zone]));
      },
      move: (id, rect: Rect) => dispatch({ type: 'move', id, rect }),
      resetWindow: (id) => {
        const element = keepFocus(id);
        dispatch({ type: 'reset-window', id });
        const zone = latest.current.state.layout.windows[id]?.zone;
        const fallback = reducerContext.current.defaults.windows[id]?.zone ?? zone ?? 'bottom';
        announce(s.announce.docked(title(id), fallback));
        after(id, element);
      },
      resetLayout: () => {
        dispatch({ type: 'reset' });
        // The designer's inspector width is part of the layout too (spec 6.3.4).
        if (user) writeInspectorWidth(user, null);
        announce(s.announce.reset);
      },
      dockAll: () => {
        dispatch({ type: 'dock-all' });
        announce(s.announce.dockedAll);
      },
      hideAll: () => {
        const focused = focusedWindow();
        dispatch({ type: 'hide-all' });
        announce(s.announce.hiddenAll);
        if (focused) setFocusIntent((f) => ({ intent: null, restore: focused, n: f.n + 1 }));
      },
      setView: (id, view) => dispatch({ type: 'view', id, view }),
      announce,
      register,
      setBadge,
      provider: providerOf,
      host,
    };
  }, [
    definitions,
    state,
    effective,
    available,
    badges,
    narrow,
    phone,
    sheet,
    workspace,
    setWorkspace,
    dispatch,
    announce,
    focusWindow,
    title,
    register,
    setBadge,
    providerOf,
    host,
    user,
  ]);

  useDockShortcuts(api);

  return (
    <DockContext.Provider value={api}>
      {children}
      <div role="status" aria-live="polite" className="sr-only" data-testid="dock-announcer">
        <span key={announcement.n}>{announcement.text}</span>
      </div>
    </DockContext.Provider>
  );
}

/** The tool-window shortcuts of spec 6.1.12 and 6.8.6. */
function useDockShortcuts(api: DockApi) {
  const ref = useRef(api);
  useEffect(() => {
    ref.current = api;
  });
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || modalOpen()) return;
      const dock = ref.current;
      const ctrl = event.ctrlKey || event.metaKey;
      if (ctrl && event.shiftKey && !event.altKey) {
        const digit = /^(?:Digit|Numpad)([1-9])$/.exec(event.code)?.[1];
        if (digit) {
          const definition = dock.definitions.find((d) => d.shortcut === Number(digit));
          if (!definition) return;
          event.preventDefault();
          dock.toggle(definition.id);
          return;
        }
      }
      const focused = focusedWindow();
      if (!focused) return;
      if (event.key === 'Escape' && event.shiftKey && !ctrl && !event.altKey) {
        event.preventDefault();
        dock.hide(focused);
        return;
      }
      if (!ctrl || !event.shiftKey || event.altKey) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        dock.toggleMaximize(focused);
        return;
      }
      const target = ARROW_ZONES[event.key];
      if (target && !isTypingTarget(event.target)) {
        event.preventDefault();
        if (target === 'float') dock.float(focused);
        else dock.dock(focused, target);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
